import React, { useState, useEffect } from 'react';
import axios from "axios";
import { toast } from 'react-toastify';
import { Plus } from 'lucide-react';

import styles from './Pillars.module.scss';
import Spinner from '../../components/spinner';
import PillarCard from './pillarCard';
import AddEditPopup from './addEditPopup';
import { useLanguage } from '../../context/languageContext';
import { Can } from '../../context/permissionContext';
import { confirmCascadeDelete } from "../../helper/confirmCascadeDelete";

const API = process.env.REACT_APP_API_ROOT;

/**
 * Pillars — the top level of the content taxonomy, with its sub-pillars nested
 * inside each pillar.
 *
 * Deliberately not the flat table the other taxonomy pages use: the whole point
 * of this screen is the parent/child relationship, so each pillar is a card and
 * its sub-pillars are managed inline on that card. Adding a sub-pillar is one
 * click and one field, not a second modal.
 *
 * Sub-pillar writes return the full updated pillar, so a card patches itself
 * from the response instead of the page re-fetching everything on every keystroke.
 */
function Pillars() {
    const { adminInfo } = useLanguage();

    const [loading, setLoading] = useState(false);
    const [pillars, setPillars] = useState([]);
    // false = closed, null = create, id = edit that pillar.
    const [editing, setEditing] = useState(false);

    const fetchPillars = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.get(`${API}/api/admin/pillar/getall`, { headers });
            if (response.status) {
                setPillars(response?.data || []);
            } else {
                toast.error(response.message || "Could not load pillars.");
            }
        } catch (error) {
            console.error("fetchPillars catch error::", error);
            toast.error(error.response?.data?.error || "Could not load pillars.");
        } finally {
            setLoading(false);
        }
    };

    // Swap one pillar in place after a sub-pillar write, keeping the list order.
    const handlePillarUpdated = (updated) => {
        setPillars((current) =>
            current.map((pillar) =>
                pillar.pillarId === updated.pillarId ? updated : pillar
            )
        );
    };

    const handleDelete = async (pillar) => {
        const headers = { Authorization: `Bearer ${adminInfo.token}` };
        if (!(await confirmCascadeDelete({
            source: "pillar",
            ids: [pillar.pillarId],
            what: `the pillar "${pillar.name}"`,
            note: pillar.subPillarCount > 0 ? `Its ${pillar.subPillarCount} sub pillar(s) go with it.` : "",
            headers,
        }))) return;

        try {
            const { data: response } = await axios.put(`${API}/api/admin/pillar/delete/${pillar.pillarId}`, {}, { headers });
            if (response.status) {
                toast.success(response.message || `"${pillar.name}" deleted.`);
                fetchPillars();
            } else {
                toast.error(response.message || "Could not delete the pillar.");
            }
        } catch (error) {
            console.error("handleDelete catch error::", error);
            toast.error(error.response?.data?.error || "Could not delete the pillar.");
        }
    };

    useEffect(() => {
        if (adminInfo?.token) fetchPillars();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo]);

    return (
        <React.Fragment>
            <div className="heading">
                <div>
                    <h4 className="mb-0">Pillars{pillars.length > 0 ? ` (${pillars.length})` : ""}</h4>
                    <small className="text-muted">
                        The content taxonomy. Add a pillar, then add sub pillars inside it —
                        posts are filed under a sub pillar.
                    </small>
                </div>
                <Can feature="pillars" action="create">
                    <button
                        type="button"
                        className="btn btn-dark d-flex align-items-center gap-2"
                        onClick={() => setEditing(null)}
                    >
                        <Plus size={18} /> Add Pillar
                    </button>
                </Can>
            </div>

            {loading ? (
                <Spinner />
            ) : (
                <div className={styles.container}>
                    {pillars.length === 0 ? (
                        <div className={styles.empty}>
                            No pillars yet. Add one, then add sub pillars inside it.
                        </div>
                    ) : (
                        <div className={styles.grid}>
                            {pillars.map((pillar) => (
                                <PillarCard
                                    key={pillar.pillarId}
                                    pillar={pillar}
                                    onEdit={() => setEditing(pillar.pillarId)}
                                    onDelete={() => handleDelete(pillar)}
                                    onPillarUpdated={handlePillarUpdated}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {editing !== false && (
                <AddEditPopup
                    key={editing ?? "new"}
                    isVisible
                    pillarId={editing}
                    pillar={pillars.find((p) => p.pillarId === editing) || null}
                    handleCloseModal={() => setEditing(false)}
                    onSuccess={fetchPillars}
                />
            )}
        </React.Fragment>
    );
}

export default Pillars;
